import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { ModuleGuard } from 'components/ModuleGuard';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Collapse,
  Row,
} from 'reactstrap';
import { currentChartTypeAtom } from 'views/state';

import CurrentCharts from './Charts/CurrentFindings';
import DashboardFilters from './Charts/DashboardFilters';
import HistoryCharts from './Charts/TrendingHistory';
import IntegrationListing from './IntegrationListing';
import {
  currentChartsOpenAtom,
  bpaRefreshAtom,
  dashboardSourceDataLoadableAtom,
  historyChartsOpenAtom,
  intervalAtom,
  showHistoryChartCountsAtom,
  timeSpanAtom,
} from './state';
import BPAGuardFallback from '../components/BPA/BPAComingSoon';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import { ChartType, DashboardType, ModalDialogData } from '../types';
import '../styles.css';
import './styles.css';

const BPAListingAutoScroll = ({
  listingScrollRef,
}: {
  listingScrollRef: React.RefObject<HTMLDivElement>;
}) => {
  const dashboardData = useAtomValue(dashboardSourceDataLoadableAtom);
  const chartsReady = dashboardData.state !== 'loading';

  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    const scrollToListing = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (!chartsReady || window.location.hash !== `#${DashboardType.BPA}`) {
        return;
      }

      scrollTimeout = setTimeout(() => {
        if (window.location.hash === `#${DashboardType.BPA}`) {
          listingScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    };

    scrollToListing();
    window.addEventListener('hashchange', scrollToListing);
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener('hashchange', scrollToListing);
    };
  }, [chartsReady, listingScrollRef]);

  return null;
};

const BPADashboard = () => {
  const [currentChartType, setCurrentChartType] = useAtom(currentChartTypeAtom);
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentChartsOpen, setCurrentChartsOpen] = useAtom(
    currentChartsOpenAtom,
  );
  const invalidateDashboardData = useSetAtom(bpaRefreshAtom);
  const listingScrollRef = useRef<HTMLDivElement>(null);
  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  useEffect(
    () => () => invalidateDashboardData((value) => value + 1),
    [invalidateDashboardData],
  );

  return (
    <ModuleGuard module="bpa" fallback={<BPAGuardFallback isCompact />}>
      <Fragment>
        <BPAListingAutoScroll listingScrollRef={listingScrollRef} />
        <div className="container-fluid p-0">
          <DashboardFilters />

          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col className="col-auto">
                  <CardTitle tag="h4">
                    Findings Trending History&nbsp;
                    <Button
                      outline
                      color="primary"
                      size="sm"
                      onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                    >
                      <i
                        className={
                          historyChartsOpen
                            ? 'bi bi-chevron-up'
                            : 'bi bi-chevron-down'
                        }
                      ></i>
                    </Button>
                  </CardTitle>
                </Col>
                <Col>
                  <Collapse isOpen={historyChartsOpen}>
                    <div className="d-inline float-right">
                      <HistoryChartHeader
                        intervalAtom={intervalAtom}
                        showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                        timeSpanAtom={timeSpanAtom}
                      />
                    </div>
                  </Collapse>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={historyChartsOpen}>
              <CardBody>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingScreen />}>
                    <HistoryCharts />
                  </Suspense>
                </ErrorBoundary>
              </CardBody>
            </Collapse>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col>
                  <CardTitle tag="h4">
                    Current Findings&nbsp;
                    <Button
                      outline
                      color="primary"
                      size="sm"
                      onClick={() => setCurrentChartsOpen(!currentChartsOpen)}
                    >
                      <i
                        className={
                          currentChartsOpen
                            ? 'bi bi-chevron-up'
                            : 'bi bi-chevron-down'
                        }
                      ></i>
                    </Button>
                  </CardTitle>
                </Col>
                <Col>
                  <Collapse isOpen={currentChartsOpen}>
                    <div className="d-inline float-right">
                      <IconButton
                        id="bpaChartTypeButton"
                        iconClass={
                          currentChartType === ChartType.Bar
                            ? 'fa fa-chart-pie fa-lg'
                            : 'fa fa-chart-bar fa-lg'
                        }
                        tooltip={
                          currentChartType === ChartType.Bar
                            ? 'Display Pie Charts'
                            : 'Display Bar Charts'
                        }
                        onClick={() =>
                          setCurrentChartType(
                            currentChartType === ChartType.Bar
                              ? ChartType.Doughnut
                              : ChartType.Bar,
                          )
                        }
                      />
                    </div>
                  </Collapse>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={currentChartsOpen}>
              <CardBody>
                <div className="row">
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingScreen />}>
                      <CurrentCharts
                        setModalComponentData={setModalComponentData}
                      />
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </CardBody>
            </Collapse>
          </Card>

          <div ref={listingScrollRef} className="mt-3">
            <Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary>
                <IntegrationListing />
              </ErrorBoundary>
            </Suspense>
          </div>
        </div>
        <ModalDialog
          title={modalComponentData?.title}
          subTitle={modalComponentData?.subTitle}
          childComponent={modalComponentData?.bodyComponent}
          size="xl"
          className="bpa-top-findings-modal"
          style={{ width: '50vw', maxWidth: '50vw' }}
          onClose={() => setModalComponentData(undefined)}
        />
      </Fragment>
    </ModuleGuard>
  );
};

export default BPADashboard;
